// Plan import. PDFs are rendered to a PNG raster entirely in the browser via
// pdf.js (Apache-2.0); PNG/JPG/WebP uploads are passed through unchanged.
// Nothing here talks to a server or a CDN — the worker is bundled by Vite.
import * as pdfjsLib from "pdfjs-dist";
import PdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";

pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

/** Target raster width for imported PDF pages — enough for crisp A3 printing. */
const PDF_TARGET_WIDTH = 2400;
const PDF_MAX_SCALE = 4;

export interface PreparedPlan {
  blob: Blob;
  mime: string;
  width: number;
  height: number;
  /** PDF page size in points (1/72") — enables scale-ratio derivation. null for images. */
  pdfPagePt: { w: number; h: number } | null;
}

function drawScaledPreview(
  source: CanvasImageSource,
  srcW: number,
  srcH: number,
  preview: HTMLCanvasElement
): void {
  const maxW = 520;
  const maxH = 240;
  const scale = Math.min(maxW / srcW, maxH / srcH, 1);
  preview.width = Math.max(1, Math.round(srcW * scale));
  preview.height = Math.max(1, Math.round(srcH * scale));
  const ctx = preview.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, preview.width, preview.height);
  ctx.drawImage(source, 0, 0, preview.width, preview.height);
}

async function prepareImage(file: File, preview: HTMLCanvasElement): Promise<PreparedPlan> {
  const bitmap = await createImageBitmap(file);
  drawScaledPreview(bitmap, bitmap.width, bitmap.height, preview);
  const result: PreparedPlan = {
    blob: file,
    mime: file.type,
    width: bitmap.width,
    height: bitmap.height,
    pdfPagePt: null,
  };
  bitmap.close();
  return result;
}

async function preparePdf(file: File, preview: HTMLCanvasElement): Promise<PreparedPlan> {
  const data = new Uint8Array(await file.arrayBuffer());
  const doc = await pdfjsLib.getDocument({ data }).promise;
  const page = await doc.getPage(1);

  const base = page.getViewport({ scale: 1 }); // width/height in PDF points
  const scale = Math.min(PDF_TARGET_WIDTH / base.width, PDF_MAX_SCALE);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D-Kontext nicht verfügbar.");
  await page.render({ canvasContext: ctx, viewport }).promise;

  drawScaledPreview(canvas, canvas.width, canvas.height, preview);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png")
  );
  if (!blob) throw new Error("PDF konnte nicht in ein Bild umgewandelt werden.");

  await doc.destroy();
  return {
    blob,
    mime: "image/png",
    width: canvas.width,
    height: canvas.height,
    pdfPagePt: { w: base.width, h: base.height },
  };
}

export async function preparePlan(
  file: File,
  preview: HTMLCanvasElement
): Promise<PreparedPlan> {
  if (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
    return preparePdf(file, preview);
  }
  return prepareImage(file, preview);
}

/**
 * Derive pixels-per-meter from a plan-drawing scale ratio (e.g. "1:250") plus
 * the PDF page's physical size. Returns null when it can't be computed.
 */
export function derivePxPerMeter(
  ratioText: string,
  renderedWidthPx: number,
  pagePt: { w: number; h: number } | null
): number | null {
  if (!pagePt) return null;
  const m = /^\s*1\s*[:/]\s*(\d+(?:[.,]\d+)?)\s*$/.exec(ratioText);
  if (!m) return null;
  const denom = parseFloat(m[1]!.replace(",", "."));
  if (!denom || denom <= 0) return null;
  // Page width in metres on paper → real-world width = paper width * denom.
  const paperWidthMeters = (pagePt.w / 72) * 0.0254;
  const realWidthMeters = paperWidthMeters * denom;
  if (realWidthMeters <= 0) return null;
  return renderedWidthPx / realWidthMeters;
}
